import { useState } from "react";
import { AdminUserRow } from "../../lib/adminApi";
import { shortId } from "../../lib/adminLabels";

interface Props {
  /** All users (already loaded by the parent). Filtered locally as you type. */
  users: AdminUserRow[];
  /** Currently selected user_id ("" = no filter / all users). */
  value: string;
  /** Called with a real user_id when one is picked, or "" when cleared. */
  onChange: (userId: string) => void;
}

/**
 * Type-to-search user filter for the audit log.
 *
 * Why this shape: the audit's user filter targets `actor_id`, a uuid column.
 * A free-text box let a partial value (e.g. "g") reach the DB and 500, and
 * asked the operator to know a 36-char UUID. Here the *typed text* only narrows
 * the visible list locally — it is NEVER sent to the server. Only a selected
 * real user's id flows out via onChange, so malformed input is impossible by
 * construction, and the operator searches by the human-meaningful email.
 *
 * Scales to hundreds of users (top-N matches shown) where a plain dropdown
 * would be unusable.
 */
export function AdminUserFilter({ users, value, onChange }: Props) {
  const [query, setQuery] = useState("");

  const selected = value ? users.find((u) => u.user_id === value) : undefined;

  // Selected state: show a chip with the email + a clear (×) button.
  // Keyed on `value`, not the looked-up object, so the clear affordance is
  // always present whenever a filter is active — even if the user list failed
  // to load or doesn't contain the id (we then label it with the short id).
  if (value) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          border: "1px solid var(--border, #2a2a2a)",
          borderRadius: 6,
          width: "fit-content",
        }}
      >
        <span style={{ fontSize: 13 }}>
          {selected?.email ?? shortId(value)}
        </span>
        <button
          type="button"
          aria-label="Clear user filter"
          onClick={() => {
            onChange("");
            setQuery("");
          }}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-muted)",
            cursor: "pointer",
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  // Match on email primarily, but also on user_id so an email-less user (shown
  // by their short id) is still reachable by typing that id — the fallback
  // label always corresponds to something typeable.
  const matches = q
    ? users
        .filter(
          (u) =>
            (u.email ?? "").toLowerCase().includes(q) ||
            u.user_id.toLowerCase().includes(q),
        )
        .slice(0, 8)
    : [];

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Type to search by email…"
        aria-label="Filter by user"
        style={{ width: "auto", padding: "6px 10px" }}
      />
      {q && matches.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 10,
            margin: "4px 0 0",
            padding: 0,
            listStyle: "none",
            minWidth: 240,
            maxHeight: 280,
            overflowY: "auto",
            background: "var(--surface, #161616)",
            border: "1px solid var(--border, #2a2a2a)",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          }}
        >
          {matches.map((u) => (
            <li key={u.user_id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => {
                  onChange(u.user_id);
                  setQuery("");
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "start",
                  background: "none",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  padding: "8px 10px",
                  fontSize: 13,
                }}
              >
                {u.email ?? shortId(u.user_id)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {q && matches.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            padding: "8px 10px",
            fontSize: 13,
            color: "var(--text-muted)",
            background: "var(--surface, #161616)",
            border: "1px solid var(--border, #2a2a2a)",
            borderRadius: 6,
          }}
        >
          No matching users
        </div>
      )}
    </div>
  );
}
